import React, { useState, useEffect, useCallback } from 'react';
import { getAccessToken } from '../../utils/auth';

const CLAIMS_API_URL = 'https://claims.adjusterphoto.com';

// Helper to make authenticated requests to the claims-sync-api
async function claimsFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const token = getAccessToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };
  if (token) {
    headers['Authorization'] = 'Bearer ' + token;
  }
  return fetch(CLAIMS_API_URL + path, { ...options, headers });
}

interface FunctionPrompt {
  enabled: boolean;
  system_instruction: string;
  prompt_template: string;
}

interface AIPromptsConfig {
  global_system_instruction: string;
  email_tones: {
    professional: string;
    friendly: string;
    formal: string;
    empathetic: string;
  };
  warnings: {
    show_warnings: boolean;
    accuracy_disclaimer: string;
    carrier_guideline_warning: string;
  };
  source_citations: {
    enabled: boolean;
    instruction: string;
  };
  prompts: Record<string, FunctionPrompt>;
}

const FUNCTION_LABELS: Record<string, string> = {
  research_property: 'Research Property',
  analyze_claim_notes: 'Analyze Claim Notes',
  draft_email: 'Draft Email',
  suggest_next_steps: 'Suggest Next Steps',
  ask_question: 'Ask Question',
  research_weather: 'Research Weather',
  analyze_policy: 'Analyze Policy',
};

const SectionCard: React.FC<{
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}> = ({ title, children, defaultExpanded = false }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div style={{ border: '1px solid #27272a', borderRadius: '8px', marginBottom: '16px', overflow: 'hidden' }}>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          background: '#18181b',
          border: 'none',
          color: '#e4e4e7',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 600,
        }}
      >
        <span>{title}</span>
        <span
          style={{
            transition: 'transform 0.2s',
            transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
            fontSize: '12px',
          }}
        >
          ▼
        </span>
      </button>
      {expanded && (
        <div style={{ padding: '16px', background: '#09090b' }}>
          {children}
        </div>
      )}
    </div>
  );
};

const AIPromptsManager: React.FC = () => {
  const [config, setConfig] = useState<AIPromptsConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadPrompts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await claimsFetch('/admin/ai-prompts');
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          throw new Error('Authentication failed. Try logging out and back in.');
        }
        throw new Error('Failed to load prompts (HTTP ' + res.status + ')');
      }
      const data = await res.json();
      setConfig(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load AI prompts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPrompts();
  }, [loadPrompts]);

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await claimsFetch('/admin/ai-prompts', {
        method: 'PUT',
        body: JSON.stringify(config),
      });
      if (!res.ok) {
        throw new Error('Failed to save prompts (HTTP ' + res.status + ')');
      }
      setSuccess('Prompts saved and propagated to all users.');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err: any) {
      setError(err.message || 'Failed to save prompts');
    } finally {
      setSaving(false);
    }
  };

  const handleResetAll = async () => {
    if (!window.confirm('Reset ALL prompts to defaults? This will overwrite all customizations.')) {
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await claimsFetch('/admin/ai-prompts/reset', { method: 'POST' });
      if (!res.ok) {
        throw new Error('Failed to reset prompts (HTTP ' + res.status + ')');
      }
      const data = await res.json();
      setConfig(data.prompts);
      setSuccess('All prompts reset to defaults and propagated.');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err: any) {
      setError(err.message || 'Failed to reset prompts');
    } finally {
      setSaving(false);
    }
  };

  const handleResetFunction = async (name: string) => {
    if (!window.confirm('Reset "' + FUNCTION_LABELS[name] + '" to its default?')) {
      return;
    }
    setError('');
    setSuccess('');
    try {
      const res = await claimsFetch('/admin/ai-prompts/reset/' + name, { method: 'POST' });
      if (!res.ok) {
        throw new Error('Failed to reset function (HTTP ' + res.status + ')');
      }
      const data = await res.json();
      // Update local state with the reset function config
      if (config && data.prompt) {
        setConfig({
          ...config,
          prompts: {
            ...config.prompts,
            [name]: data.prompt,
          },
        });
      }
      setSuccess('"' + FUNCTION_LABELS[name] + '" reset to default.');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err: any) {
      setError(err.message || 'Failed to reset function');
    }
  };

  const updateField = (path: string[], value: any) => {
    if (!config) return;
    const newConfig = JSON.parse(JSON.stringify(config));
    let obj: any = newConfig;
    for (let i = 0; i < path.length - 1; i++) {
      obj = obj[path[i]];
    }
    obj[path[path.length - 1]] = value;
    setConfig(newConfig);
  };

  if (loading) {
    return <div style={{ color: '#71717a', padding: '24px', textAlign: 'center' }}>Loading AI prompts...</div>;
  }

  if (!config) {
    return (
      <div>
        {error && (
          <div style={{
            background: '#450a0a',
            border: '1px solid #991b1b',
            borderRadius: '8px',
            padding: '12px 16px',
            color: '#fca5a5',
            marginBottom: '16px',
          }}>
            {error}
          </div>
        )}
        <button
          onClick={loadPrompts}
          style={{
            padding: '8px 16px',
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  const textareaStyle: React.CSSProperties = {
    width: '100%',
    minHeight: '120px',
    padding: '10px',
    background: '#18181b',
    border: '1px solid #27272a',
    borderRadius: '6px',
    color: '#e4e4e7',
    fontFamily: 'monospace',
    fontSize: '13px',
    resize: 'vertical',
    lineHeight: '1.5',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    color: '#a1a1aa',
    fontSize: '13px',
    fontWeight: 500,
    marginBottom: '6px',
  };

  const fieldGroupStyle: React.CSSProperties = {
    marginBottom: '16px',
  };

  return (
    <div>
      {/* Status Messages */}
      {error && (
        <div style={{
          background: '#450a0a',
          border: '1px solid #991b1b',
          borderRadius: '8px',
          padding: '12px 16px',
          color: '#fca5a5',
          marginBottom: '16px',
        }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{
          background: '#052e16',
          border: '1px solid #166534',
          borderRadius: '8px',
          padding: '12px 16px',
          color: '#86efac',
          marginBottom: '16px',
        }}>
          {success}
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '10px 24px',
            background: saving ? '#1e3a5f' : '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontWeight: 600,
            fontSize: '14px',
          }}
        >
          {saving ? 'Saving...' : 'Save All & Propagate'}
        </button>
        <button
          onClick={handleResetAll}
          disabled={saving}
          style={{
            padding: '10px 24px',
            background: 'transparent',
            color: '#ef4444',
            border: '1px solid #ef4444',
            borderRadius: '6px',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontWeight: 500,
            fontSize: '14px',
          }}
        >
          Reset All to Defaults
        </button>
      </div>

      {/* Global System Instruction */}
      <SectionCard title="Global System Instruction" defaultExpanded={true}>
        <div style={fieldGroupStyle}>
          <label style={labelStyle}>
            System instruction applied to all AI functions
          </label>
          <textarea
            style={{ ...textareaStyle, minHeight: '160px' }}
            value={config.global_system_instruction || ''}
            onChange={(e) => updateField(['global_system_instruction'], e.target.value)}
          />
        </div>
      </SectionCard>

      {/* Email Tones */}
      <SectionCard title="Email Tones">
        {config.email_tones && Object.entries(config.email_tones).map(([tone, instruction]) => (
          <div key={tone} style={fieldGroupStyle}>
            <label style={labelStyle}>
              {tone.charAt(0).toUpperCase() + tone.slice(1)}
            </label>
            <textarea
              style={textareaStyle}
              value={instruction || ''}
              onChange={(e) => updateField(['email_tones', tone], e.target.value)}
            />
          </div>
        ))}
      </SectionCard>

      {/* Warning Settings */}
      <SectionCard title="Warning Settings">
        <div style={{ ...fieldGroupStyle, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            type="checkbox"
            checked={config.warnings?.show_warnings ?? true}
            onChange={(e) => updateField(['warnings', 'show_warnings'], e.target.checked)}
            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
          />
          <label style={{ ...labelStyle, marginBottom: 0 }}>Show warnings</label>
        </div>
        <div style={fieldGroupStyle}>
          <label style={labelStyle}>Accuracy Disclaimer</label>
          <textarea
            style={textareaStyle}
            value={config.warnings?.accuracy_disclaimer || ''}
            onChange={(e) => updateField(['warnings', 'accuracy_disclaimer'], e.target.value)}
          />
        </div>
        <div style={fieldGroupStyle}>
          <label style={labelStyle}>Carrier Guideline Warning</label>
          <textarea
            style={textareaStyle}
            value={config.warnings?.carrier_guideline_warning || ''}
            onChange={(e) => updateField(['warnings', 'carrier_guideline_warning'], e.target.value)}
          />
        </div>
      </SectionCard>

      {/* Source Citations */}
      <SectionCard title="Source Citations">
        <div style={{ ...fieldGroupStyle, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            type="checkbox"
            checked={config.source_citations?.enabled ?? true}
            onChange={(e) => updateField(['source_citations', 'enabled'], e.target.checked)}
            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
          />
          <label style={{ ...labelStyle, marginBottom: 0 }}>Enable source citations</label>
        </div>
        <div style={fieldGroupStyle}>
          <label style={labelStyle}>Citation Instruction</label>
          <textarea
            style={textareaStyle}
            value={config.source_citations?.instruction || ''}
            onChange={(e) => updateField(['source_citations', 'instruction'], e.target.value)}
          />
        </div>
      </SectionCard>

      {/* Function Prompts */}
      {config.prompts && Object.entries(config.prompts).map(([name, func]) => {
        const fn = func as FunctionPrompt;
        return (
          <SectionCard key={name} title={FUNCTION_LABELS[name] || name}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={fn.enabled ?? true}
                  onChange={(e) => updateField(['prompts', name, 'enabled'], e.target.checked)}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <label style={{ ...labelStyle, marginBottom: 0 }}>Enabled</label>
              </div>
              <button
                onClick={() => handleResetFunction(name)}
                style={{
                  padding: '4px 12px',
                  background: 'transparent',
                  color: '#a1a1aa',
                  border: '1px solid #3f3f46',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                Reset to Default
              </button>
            </div>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>System Instruction</label>
              <textarea
                style={{ ...textareaStyle, minHeight: '150px' }}
                value={fn.system_instruction || ''}
                onChange={(e) => updateField(['prompts', name, 'system_instruction'], e.target.value)}
              />
            </div>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Prompt Template</label>
              <textarea
                style={{ ...textareaStyle, minHeight: '200px' }}
                value={fn.prompt_template || ''}
                onChange={(e) => updateField(['prompts', name, 'prompt_template'], e.target.value)}
              />
            </div>
          </SectionCard>
        );
      })}

      {/* Bottom Save Button */}
      <div style={{ display: 'flex', gap: '12px', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #27272a' }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '10px 24px',
            background: saving ? '#1e3a5f' : '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontWeight: 600,
            fontSize: '14px',
          }}
        >
          {saving ? 'Saving...' : 'Save All & Propagate'}
        </button>
        <button
          onClick={handleResetAll}
          disabled={saving}
          style={{
            padding: '10px 24px',
            background: 'transparent',
            color: '#ef4444',
            border: '1px solid #ef4444',
            borderRadius: '6px',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontWeight: 500,
            fontSize: '14px',
          }}
        >
          Reset All to Defaults
        </button>
      </div>
    </div>
  );
};

export default AIPromptsManager;
